import i18next from 'i18next';

import {isEmpty} from 'web-app/util/array';
import {useModal} from 'web-app/react/modal-context/modal-context';
import * as GlobalEvent from 'web-app/services/global-event';
import * as SentryService from 'web-app/services/sentry';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {useFetchFinanceFeaturesQuery} from 'web-app/react/bookings-finance/hooks/use-fetch-finance-features-query';
import {FeatureSupportState} from 'web-app/util/graphql/api-types';
import {useFetchPlanWarnings} from 'web-app/react/simplified-us-billing-plan/hooks/use-fetch-plan-warnings';

import {type Child} from '../helpers/plan-types';
import {ScheduleBreakModal} from '../loader';

type Args = {
    institutionSetId: string;
    afterSubmission?: () => void;
};
type Result = {
    /** True iff the required feature flag is set. */
    isFeatureAvailable: boolean;

    /** True while the modal is loading. */
    isLoading: boolean;

    /**
     * Opens the Schedule a break modal for a number of children. Note that
     * it is assumed that `isFeatureAvailable` and `!isLoading`.
     * @param children A non-empty list of children to schedule breaks for.
     * @param shouldConfirm When true, shows a confirmation dialog before scheduling the break.
     * This should be set when opening the modal from the finance overview page.
     */
    open: ({children, shouldConfirm}: {children: Child[]; shouldConfirm?: boolean}) => void;
};

/**
 * Main entry point for opening Schedule a break modals.
 * @param args Customizations.
 * @returns Values relevant for opening the modal.
 */
export const useScheduleBreakModal = (args: Args): Result => {
    const containerClassName = 'schedule-break-container';
    const {show, hide} = useModal({
        component: ScheduleBreakModal,
        ignoreOutsideClickSelectors: [`.${containerClassName}`],
        modalProps: {
            containerClassName,
        },
    });

    const {fetchPlanWarnings} = useFetchPlanWarnings();

    const handleErrorMessage = (msg: string) => {
        GlobalEvent.updateError(i18next.t('somethingWentWrong'));
        SentryService.captureMessage(msg);
    };

    const {data: featureFlag, loading: featureFlagLoading} = useFetchFinanceFeaturesQuery({
        variables: {institutionSetId: args.institutionSetId},
    });
    const isFeatureAvailable = featureFlag?.finance.supports.canScheduleAPlanBreak === FeatureSupportState.ACTIVE;

    const findFirstUnbilledDate = async (children: Child[]): Promise<string | null> => {
        for (const {childId} of children) {
            try {
                const {data} = await fetchPlanWarnings({variables: {childId}});

                const plans = data?.result?.plans ?? [];

                for (const plan of plans) {
                    const warningBehavior = plan.behaviors?.find(behavior => behavior.id === 'ShowPlanWarnings');

                    const changeFrom = warningBehavior?.payload?.warnings?.[0]?.metadata?.changeFrom;

                    if (hasValue(changeFrom)) {
                        return changeFrom;
                    }
                }
            } catch (error) {
                handleErrorMessage('Unable to fetch plan warnings');
            }
        }

        return null;
    };

    const open = async ({children, shouldConfirm}: {children: Child[]; shouldConfirm?: boolean}) => {
        if (!isFeatureAvailable) {
            handleErrorMessage('Schedule a break is not available. Missing feature flag.');
            return;
        }

        if (featureFlagLoading) {
            handleErrorMessage('Schedule a break is not yet ready.');
            return;
        }

        if (isEmpty(children)) {
            handleErrorMessage('Cannot schedule break for zero children.');
            return;
        }

        const firstUnbilledDate = await findFirstUnbilledDate(children);

        show({
            children,
            siteId: args.institutionSetId,
            isLoading: false,
            onClose: () => {
                hide();
                if (hasValue(args.afterSubmission)) {
                    args.afterSubmission();
                }
            },
            shouldConfirm,
            firstUnbilledDate,
        });
    };

    return {open, isFeatureAvailable, isLoading: featureFlagLoading};
};
